import type * as React from "react";

import { cn } from "../lib/cn";

/**
 * Blue is what Brian read or proposed: `window` outlines a day matching the
 * asked range, `proposed` tints the asked start, `agreed` holds it once both
 * sides said yes, and `moving` dashes the visit that moves. `conflict` is the
 * one yellow cell: the time a person asked for is held by someone else.
 * `recorded` is written. `full` is a day or bin with nothing open.
 */
export type SlotTone =
  | "open"
  | "busy"
  | "blocked"
  | "window"
  | "proposed"
  | "agreed"
  | "conflict"
  | "moving"
  | "recorded"
  | "full";

const toneSurface: Record<SlotTone, string> = {
  open: "border border-rule bg-card",
  busy: "border border-rule bg-occupied",
  blocked: "border border-rule bg-hatch",
  window: "border-[1.5px] border-ink-brian bg-card",
  proposed: "border-[1.5px] border-ink-brian bg-tint-brian",
  agreed: "border-2 border-ink-brian bg-tint-brian",
  conflict: "border-2 border-ink-attention bg-tint-attention",
  moving: "border-[1.5px] border-dashed border-ink-brian bg-card",
  recorded: "border-[1.5px] border-ink-recorded bg-tint-recorded",
  full: "border border-rule bg-occupied",
};

const toneLabel: Record<SlotTone, string> = {
  open: "text-ink",
  busy: "text-annotation",
  blocked: "text-annotation",
  window: "text-ink",
  proposed: "font-semibold text-ink",
  agreed: "font-semibold text-ink",
  conflict: "font-semibold text-ink",
  moving: "text-ink-body",
  recorded: "font-semibold text-ink",
  full: "text-placeholder",
};

const toneSpoken: Partial<Record<SlotTone, string>> = {
  window: "in the asked range",
  proposed: "proposed",
  agreed: "agreed",
  conflict: "taken",
  moving: "moving",
  recorded: "recorded",
};

export interface SlotCellProps extends Omit<
  React.HTMLAttributes<HTMLSpanElement>,
  "children"
> {
  time: React.ReactNode;
  label?: React.ReactNode;
  tone: SlotTone;
  ref?: React.Ref<HTMLSpanElement>;
}

export function SlotCell({
  time,
  label,
  tone,
  className,
  ...rest
}: SlotCellProps) {
  const spoken = toneSpoken[tone];
  return (
    <span
      {...rest}
      className={cn(
        "flex min-w-0 flex-col gap-0.5 rounded-sheet px-2 py-1.5",
        toneSurface[tone],
        className,
      )}
    >
      <span
        className={cn(
          "text-mono-time text-xs",
          tone === "full" ? "text-placeholder" : "text-ink",
        )}
      >
        {time}
      </span>
      {label != null && (
        <span
          className={cn("text-xs leading-tight break-words", toneLabel[tone])}
        >
          {label}
        </span>
      )}
      {spoken && <span className="sr-only">, {spoken}</span>}
    </span>
  );
}
